import { GraphData, graphHeightStyle } from '../content-creator/graph-model';
import { createGraphSvg } from '../content-creator/graph-svg';

export function createGraphViewport(doc: Document, graph: GraphData, height?: number | string | null): HTMLElement {
    const viewer = doc.createElement('div');
    viewer.className = 'graph-viewer';
    const viewport = doc.createElement('div');
    viewport.className = 'graph-viewport';
    const displayHeight = graphHeightStyle(height);
    if (displayHeight !== undefined) viewport.style.height = displayHeight;
    viewport.tabIndex = 0;
    viewport.setAttribute('role', 'region');
    viewport.setAttribute('aria-label', 'Graph viewer. Drag to pan, use plus or minus to zoom, and Home to fit.');
    const svg = createGraphSvg(doc, graph, true);
    svg.style.height = '100%';
    viewport.appendChild(svg);
    const footer = doc.createElement('div');
    footer.className = 'graph-viewport-footer';
    const hint = doc.createElement('small');
    hint.textContent = 'Drag to pan · Ctrl+scroll to zoom';
    const controls = doc.createElement('div');
    controls.className = 'graph-zoom-controls';
    controls.setAttribute('role', 'toolbar');
    controls.setAttribute('aria-label', 'Graph zoom controls');
    const button = (action: string, text: string, label: string) => {
        const element = doc.createElement('button');
        element.type = 'button'; element.textContent = text;
        element.setAttribute('data-graph-zoom', action);
        element.setAttribute('aria-label', label); element.title = label;
        return element;
    };
    const level = doc.createElement('output');
    level.className = 'graph-zoom-level';
    level.setAttribute('aria-label', 'Zoom level');
    level.setAttribute('aria-live', 'polite');
    level.textContent = 'Fit';
    controls.append(button('out', '−', 'Zoom out'), level, button('in', '+', 'Zoom in'),
        button('actual', '100%', 'Actual size (100%)'), button('fit', 'Fit', 'Fit graph'));
    footer.append(hint, controls);
    viewer.append(viewport, footer);
    return viewer;
}

/** Plain iframe script: no runtime imports or serialization of transpiled functions. */
export const GRAPH_VIEWPORT_SCRIPT = String.raw`
    window.hydrateGraphs = function() {
        document.querySelectorAll('div[data-type="graph-component"] .graph-node').forEach(node => {
            const showContent = () => window.postGraphClick(node.getAttribute('data-graph-node-id'), node);
            node.onclick = showContent;
            node.onkeydown = event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    showContent();
                }
            };
        });
        document.querySelectorAll('.graph-viewer').forEach(viewer => {
            const viewport = viewer.querySelector('.graph-viewport');
            const svg = viewport && viewport.querySelector('svg');
            if (!svg || viewport.dataset.graphZoomReady) return;
            viewport.dataset.graphZoomReady = 'true';
            const original = svg.getAttribute('viewBox').trim().split(/[\s,]+/).map(Number);
            const naturalWidth = original[2], naturalHeight = original[3];
            const level = viewer.querySelector('.graph-zoom-level');
            const zoomOut = viewer.querySelector('[data-graph-zoom="out"]');
            const zoomIn = viewer.querySelector('[data-graph-zoom="in"]');
            let scale = 1, cx = naturalWidth / 2, cy = naturalHeight / 2, fitted = true;
            let drag = null, suppressClick = false;
            const bounds = () => {
                const rect = viewport.getBoundingClientRect();
                return { left: rect.left, top: rect.top, width: rect.width || naturalWidth, height: rect.height || naturalHeight };
            };
            const fitScale = () => {
                const box = bounds();
                return Math.min(1, box.width / naturalWidth, box.height / naturalHeight);
            };
            const minimum = () => Math.min(0.1, fitScale());
            const draw = () => {
                const box = bounds(), width = box.width / scale, height = box.height / scale;
                svg.setAttribute('viewBox', [cx - width / 2, cy - height / 2, width, height].join(' '));
                level.textContent = Math.round(scale * 100) + '%';
                zoomOut.disabled = scale <= minimum() + 0.00001;
                zoomIn.disabled = scale >= 4 - 0.00001;
            };
            const fit = () => { fitted = true; scale = fitScale(); cx = naturalWidth / 2; cy = naturalHeight / 2; draw(); };
            const zoom = (factor, anchor) => {
                const next = Math.max(minimum(), Math.min(4, scale * factor));
                if (anchor) {
                    const box = bounds(), dx = anchor.x - box.left - box.width / 2, dy = anchor.y - box.top - box.height / 2;
                    cx += dx / scale - dx / next;
                    cy += dy / scale - dy / next;
                }
                scale = next; fitted = false; draw();
            };
            viewer.querySelectorAll('[data-graph-zoom]').forEach(button => {
                button.onclick = () => {
                    switch (button.getAttribute('data-graph-zoom')) {
                        case 'in': zoom(1.25); break;
                        case 'out': zoom(0.8); break;
                        case 'fit': fit(); break;
                        case 'actual': fitted = false; scale = 1; cx = naturalWidth / 2; cy = naturalHeight / 2; draw(); break;
                    }
                };
            });
            viewport.addEventListener('wheel', event => {
                // Ordinary scrolling continues to scroll the lesson.
                if (!event.ctrlKey && !event.metaKey) return;
                event.preventDefault();
                if (event.deltaY) zoom(event.deltaY < 0 ? 1.25 : 0.8, { x: event.clientX, y: event.clientY });
            }, { passive: false });
            viewport.addEventListener('pointerdown', event => {
                if (event.button !== 0 || event.isPrimary === false) return;
                suppressClick = false;
                drag = { id: event.pointerId, x: event.clientX, y: event.clientY, cx, cy, moved: false };
            });
            viewport.addEventListener('pointermove', event => {
                if (!drag || drag.id !== event.pointerId) return;
                const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
                if (!drag.moved && Math.hypot(dx, dy) < 5) return;
                if (!drag.moved && viewport.setPointerCapture) viewport.setPointerCapture(event.pointerId);
                drag.moved = true; fitted = false;
                viewport.classList.add('is-panning');
                cx = drag.cx - dx / scale; cy = drag.cy - dy / scale;
                event.preventDefault(); draw();
            });
            const finishDrag = event => {
                if (!drag || drag.id !== event.pointerId) return;
                suppressClick = drag.moved && event.type !== 'pointercancel';
                drag = null;
                viewport.classList.remove('is-panning');
                if (viewport.hasPointerCapture && viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
            };
            viewport.addEventListener('pointerup', finishDrag);
            viewport.addEventListener('pointercancel', finishDrag);
            viewport.addEventListener('click', event => {
                if (!suppressClick) return;
                suppressClick = false; event.preventDefault(); event.stopPropagation();
            }, true);
            viewport.addEventListener('keydown', event => {
                if (event.target !== viewport) return;
                if (event.key === '+' || event.key === '=') zoom(1.25);
                else if (event.key === '-') zoom(0.8);
                else if (event.key === 'Home') fit();
                else if (event.key === 'ArrowLeft') { fitted = false; cx -= 40 / scale; draw(); }
                else if (event.key === 'ArrowRight') { fitted = false; cx += 40 / scale; draw(); }
                else if (event.key === 'ArrowUp') { fitted = false; cy -= 40 / scale; draw(); }
                else if (event.key === 'ArrowDown') { fitted = false; cy += 40 / scale; draw(); }
                else return;
                event.preventDefault();
            });
            if (window.ResizeObserver) {
                const observer = new window.ResizeObserver(() => fitted ? fit() : draw());
                observer.observe(viewport);
            } else if (window.addEventListener) {
                window.addEventListener('resize', () => fitted ? fit() : draw());
            }
            fit();
        });
    };
`;
